import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaSearchPlus, FaTimes } from 'react-icons/fa';
import Navbar from '../../components/Navbar/NavBar';

// Import product images
import img1 from '../../images/products/M1_P1.1.png';
import img2 from '../../images/products/M1_P1.2.png';
import img3 from '../../images/products/M1_P1.3.png';
import img4 from '../../images/products/M1_P1.4.png';

// Import PDF file
import productPDF from '../../Manuals/P1.pdf';

// Import PDF Viewer
import { Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

const Product1 = () => {
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const images = [img1, img2, img3, img4];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openPdfModal = () => {
    setIsPdfOpen(true);
  };

  const closePdfModal = () => {
    setIsPdfOpen(false);
  };

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-gray-100 flex flex-col items-center pt-24 p-6">
        {/* Centered Product Title */}
        <h1 className="text-7xl font-extrabold text-blue-900 mb-12 text-center" style={{ fontFamily: 'Congenial Black' }}>
          Data Logger
        </h1>

        <div className="flex flex-col sm:flex-row items-start w-full max-w-6xl">
          {/* Image Carousel */}
          <div className="relative w-[500px]">
            <button 
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-gray-700 text-white p-2 rounded-full transition hover:bg-gray-900" 
              onClick={prevImage}
            >
              <FaArrowLeft />
            </button>

            <img 
              src={images[currentImage]} 
              alt="Data Logger" 
              className="w-full rounded-lg shadow-lg cursor-pointer"
            />

            <button 
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-700 text-white p-2 rounded-full transition hover:bg-gray-900" 
              onClick={nextImage}
            >
              <FaArrowRight />
            </button>

            {/* Magnify Button */}
            <button 
              className="absolute top-2 right-2 bg-gray-700 text-white p-2 rounded-full transition hover:bg-gray-900"
              onClick={openModal}
            >
              <FaSearchPlus />
            </button>

            {/* PDF Button */}
            <div className="mt-4 flex justify-center">
              <button 
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-800 transition"
                onClick={openPdfModal}
              >
                View PDF
              </button>
            </div>
          </div>

          {/* Product Details - Positioned on the Right */}
          <div className="ml-10 max-w-lg bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-3 text-gray-800">Specifications</h2>
            <ul className="list-disc list-inside text-gray-700">
              <li><strong>Model:</strong> Zem Log</li>
              <li><strong>Type:</strong> Digital Data Logger</li>
              <li><strong>Usage:</strong> Cold Storages</li>
              <li><strong>Parameter:</strong> Temperature</li>
              <li><strong>Interface:</strong> USB</li>
              <li><strong>Export Data:</strong> Excel</li>
              <li><strong>Dimensions:</strong> 96 x 96 mm</li>
              <li><strong>Brand:</strong> Gravity Techno</li>
              <li><strong>Channels:</strong> 8</li>
              <li><strong>Power Supply:</strong> Electric</li>
              <li><strong>Memory:</strong> 70% Alarm</li>
              <li><strong>Frequency:</strong> 50 Hz</li>
              <li><strong>Voltage:</strong> 265 VAC (SMPS based power supply)</li>
            </ul>

            {/* Features Section */}
            <div className="mt-6">
              <h2 className="text-2xl font-semibold mb-3 text-gray-800">Key Features</h2>
              <ul className="list-disc list-inside text-gray-700">
                <li>Simple user interface with 16x2 LCD, 4 programmable keys, and status LEDs</li>
                <li>PID controller interface with RS 485 support (supports up to 2 PID controllers)</li>
                <li>Direct computer interface via RS 485 connection</li>
                <li>Password-protected settings for added security</li>
                <li>Data storage capacity: 5000 readings per channel for printer and PC</li>
                <li>Programmable logging interval (1 - 255 minutes)</li>
                <li>Supports connection of 2 ZemLog units for PC data downloading</li>
                <li>Printing options: Online, Date-wise, and Page-wise printing</li>
                <li>Direct printer interface (EPSON LX300+), cable included</li>
                <li>5V-100mA power output for direct RH sensors</li>
              </ul>
            </div>
          </div>
        </div>

        <button 
          onClick={() => navigate(-1)} 
          className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-800 transition"
        >
          Go Back
        </button>
      </div>

      {/* Modal for Enlarged Image */}
      {isModalOpen && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-75 flex justify-center items-center z-50" onClick={closeModal}>
          <div className="relative max-w-3xl">
            <img src={images[currentImage]} alt="Data Logger Enlarged" className="w-full rounded-lg"/>
            <button className="absolute top-3 right-3 bg-gray-700 text-white p-2 rounded-full transition hover:bg-gray-900" onClick={closeModal}>
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {isPdfOpen && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-75 flex justify-center items-center z-50">
          <div className="relative bg-white w-3/4 h-3/4 p-4 rounded-lg shadow-lg">
            <button className="absolute top-3 right-3 bg-gray-700 text-white p-2 rounded-full transition hover:bg-gray-900" onClick={closePdfModal}>
              <FaTimes />
            </button>
            <div className="h-full overflow-auto">
              <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.14.305/build/pdf.worker.min.js`}>
                <iframe src={productPDF} className="w-full h-full" title="Product PDF"></iframe>
              </Worker>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Product1;
